'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { getToken } from '@/lib/auth';
import {
  TAGLINE, TRIAL_DAYS, PRICE_ANNUAL, PRICE_MONTHLY,
  FREE_FEATURES, PRO_HEADLINE_FEATURES, PRO_MORE_FEATURES,
  rememberPreferredPlan, rememberSignupIntent,
} from '@/lib/proFeatures';

// ── Helpers ───────────────────────────────────────────────────────────────────

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="flex-shrink-0 mt-0.5">
      <circle cx="8" cy="8" r="8" fill="#2D5016" />
      <path d="M4.5 8L6.5 10.5L11.5 5.5" stroke="white" strokeWidth="1.5"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Header ────────────────────────────────────────────────────────────────────

function Header() {
  return (
    <header className="sticky top-0 z-40 bg-white border-b border-[#E8E0D0]">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/images/logo/Willowbottom-Logo.png" alt="" className="h-11 w-auto flex-shrink-0" />
          <div>
            <span className="font-heading text-[24px] text-[#2D5016] leading-none block">Garden</span>
            <span className="font-body text-[13px] text-[#6B8F6E] leading-none">by Willowbottom</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Link
            href="/login"
            className="font-body text-[14px] text-[#6B8F6E] hover:text-[#2D5016] transition-colors"
          >
            Sign in
          </Link>
          <Link
            href="/register"
            className="font-body text-[14px] font-semibold text-white bg-[#2D5016]
                       px-4 py-2 rounded-lg hover:opacity-90 transition-opacity"
          >
            Start free
          </Link>
        </div>
      </div>
    </header>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────────────

function Hero() {
  return (
    <section className="bg-[#FAF7F2] px-5 sm:px-8 pt-16 pb-14 sm:pt-20 sm:pb-16">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">

          {/* Text */}
          <div className="flex flex-col gap-6">
            <p className="font-body text-[13px] font-semibold text-[#6B8F6E] uppercase tracking-widest">
              Organic gardening. Simplified.
            </p>
            <div>
              <h1 className="font-heading text-[36px] sm:text-[52px] text-[#1C2B16] leading-[1.15]">
                Garden like you know
                <br />what you&rsquo;re doing.
              </h1>
              <h1 className="font-heading text-[36px] sm:text-[52px] text-[#2D5016] leading-[1.15]">
                Because now you will.
              </h1>
            </div>
            <p className="font-body text-[16px] sm:text-[18px] text-[#6B8F6E] leading-[1.65] max-w-[480px]">
              Garden by Willowbottom gives you the organic growing knowledge, daily guidance,
              and personal tools to grow with confidence - whether it&rsquo;s your first seed
              or your fiftieth season.
            </p>
            <div className="flex flex-col items-start gap-3">
              <Link
                href="/register"
                className="font-body text-[17px] font-semibold text-white bg-[#2D5016]
                           px-8 py-[14px] rounded-xl hover:opacity-90 transition-opacity
                           inline-block"
              >
                Start your garden free
              </Link>
              <p className="font-body text-[13px] text-[#6B8F6E]">
                Free to start. Pro from $5.99/month. No credit card required.
              </p>
            </div>
          </div>

          {/* Screenshots mockup */}
          <div className="relative flex justify-center lg:justify-end">
            <div className="relative w-full max-w-[540px]">

              {/* Desktop browser frame */}
              <div className="relative rounded-xl overflow-hidden shadow-2xl border border-[#E8E0D0]">
                {/* Browser chrome bar */}
                <div className="bg-[#F5F5F5] border-b border-[#E0E0E0] px-3 py-2.5 flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[#FFBD2E]" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
                  <div className="ml-3 flex-1 bg-white rounded border border-[#E0E0E0] h-5 max-w-[200px]" />
                </div>
                {/* Screenshot */}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src="/images/landing/desktop-screenshot.png"
                  alt="Garden by Willowbottom dashboard showing weather and daily garden guidance"
                  style={{ width: '100%', height: 'auto', borderRadius: '12px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}
                />
              </div>

              {/* Mobile phone frame — overlapping bottom-right */}
              <div className="absolute -bottom-6 -right-4 sm:-right-8 w-[30%] max-w-[160px]
                              rounded-[20px] overflow-hidden shadow-xl border border-[#E8E0D0]
                              bg-white z-10">
                {/* Phone notch */}
                <div className="bg-[#1C2B16] h-5 flex items-center justify-center">
                  <div className="w-12 h-2 rounded-full bg-[#0D1A0A]" />
                </div>
                {/* Screenshot */}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src="/images/landing/mobile-screenshot.png"
                  alt="Garden by Willowbottom on mobile showing frost warnings and daily tasks"
                  style={{ width: '100%', height: 'auto', borderRadius: '24px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}
                />
                {/* Home bar */}
                <div className="bg-white h-4 flex items-center justify-center">
                  <div className="w-10 h-1 rounded-full bg-[#D0D0D0]" />
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Trust bar ─────────────────────────────────────────────────────────────────

function TrustBar() {
  const items = [
    'Organic methods only - always',
    'Soil-first, ecological approach',
    'No gatekeeping. No jargon.',
  ];
  return (
    <section className="bg-white border-b border-[#E8E0D0] py-5 px-5 sm:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
          {items.map(text => (
            <span
              key={text}
              className="font-body text-[14px] text-[#6B8F6E] flex items-center gap-2"
            >
              <span className="w-2 h-2 rounded-full bg-[#2D5016] flex-shrink-0 inline-block" />
              {text}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Problem section ───────────────────────────────────────────────────────────

const PROBLEMS = [
  {
    heading: 'Conflicting advice from every direction',
    lead: 'One source says full sun. Another says partial shade. Your neighbor swears by something else. It\u2019s confusing.',
    text: 'Garden cuts through the noise with clear, consistent guidance.',
  },
  {
    heading: 'The "master gardener" problem',
    lead: 'Gardening can feel like a club you\u2019re not part of.',
    text: 'Garden is built for real people. No credentials required.',
  },
  {
    heading: 'Generic advice that ignores where you live',
    lead: 'Most advice isn\u2019t written for your conditions.',
    text: 'Garden uses your location, your weather, and your soil to guide real decisions.',
  },
  {
    heading: 'Not knowing what to do today',
    lead: 'The hardest part isn\u2019t planting. It\u2019s knowing what matters right now.',
    text: 'Garden tells you exactly what to do today.',
  },
];

function Problems() {
  return (
    <section className="bg-[#F0F4F0] px-5 sm:px-8 py-16 sm:py-24">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="font-heading text-[28px] sm:text-[36px] text-[#1C2B16] leading-[1.25]">
            Gardening advice is everywhere.
            <br />
            <span className="text-[#2D5016]">Good</span> gardening advice is rare.
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {PROBLEMS.map(p => (
            <div
              key={p.heading}
              className="bg-white border border-[#E8E0D0] rounded-xl p-6"
            >
              <div className="flex items-start gap-3 mb-2">
                <div className="w-2.5 h-2.5 rounded-full bg-[#2D5016] flex-shrink-0 mt-[0.45rem]" />
                <h3 className="font-heading text-[19px] text-[#1C2B16] leading-snug">
                  {p.heading}
                </h3>
              </div>
              <p className="font-body text-[15px] text-[#6B8F6E] leading-relaxed">
                <strong className="font-semibold text-[#1C2B16]">{p.lead}</strong>
                {' '}
                {p.text}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Features section ──────────────────────────────────────────────────────────

const FEATURES = [
  {
    heading: 'What to do today',
    desc: 'Know exactly what your garden needs today. Weather-aware guidance tells you when to water, plant, or wait.',
  },
  {
    heading: 'Ask Garden anything',
    desc: 'Get clear answers about your plants, soil, pests, and growing conditions - no guessing.',
  },
  {
    heading: 'Photo diagnosis',
    desc: 'Upload a photo and get a clear diagnosis with organic, practical solutions.',
  },
  {
    heading: 'Garden templates',
    desc: 'Start with proven garden layouts - vegetables, herbs, or mixed beds - ready to plant.',
  },
  {
    heading: 'Pest and disease identifier',
    desc: 'Identify problems quickly and get safe, effective treatment options.',
  },
  {
    heading: 'Deep ecological knowledge',
    desc: 'Understand soil, plants, and ecosystems so you can grow stronger, more resilient gardens.',
  },
];

function Features() {
  return (
    <section className="bg-white px-5 sm:px-8 py-16 sm:py-24">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="font-heading text-[28px] sm:text-[36px] text-[#1C2B16] mb-3">
            Everything you need to grow well
          </h2>
          <p className="font-body text-[16px] sm:text-[18px] text-[#6B8F6E]">
            Built around your actual garden, not a generic one
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {FEATURES.map(f => (
            <div key={f.heading} className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 flex-shrink-0 rounded-full bg-[#2D5016]" />
                <h3 className="font-heading text-[20px] text-[#1C2B16] leading-snug">{f.heading}</h3>
              </div>
              <p className="font-body text-[15px] text-[#6B8F6E] leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Philosophy section ────────────────────────────────────────────────────────

const PHILOSOPHY_PILLS = [
  'Organic methods only',
  'Soil-first approach',
  'Inspired by Robin Wall Kimmerer, Wendell Berry, and Aldo Leopold',
  'In-ground gardening first',
  'No synthetic pesticides. Ever.',
];

function Philosophy() {
  return (
    <section className="bg-[#FAF7F2] px-5 sm:px-8 py-16 sm:py-24">
      <div className="max-w-6xl mx-auto flex flex-col items-center text-center gap-8">
        <blockquote className="font-heading italic text-[20px] sm:text-[24px] text-[#1C2B16]
                               leading-[1.55] max-w-[700px]">
          &ldquo;The knowledge to grow food belongs to everyone. Not just people with credentials.
          Not just people with perfect soil. Everyone.&rdquo;
        </blockquote>
        <p className="font-body text-[14px] text-[#6B8F6E]">- Richard, Willowbottom Homestead</p>
        <div className="flex flex-wrap justify-center gap-2 max-w-2xl">
          {PHILOSOPHY_PILLS.map(pill => (
            <span
              key={pill}
              className="font-body text-[13px] text-[#6B8F6E] bg-[#F0F4F0]
                         border border-[#C5D9B8] px-3.5 py-1.5 rounded-full"
            >
              {pill}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Pricing section ───────────────────────────────────────────────────────────

function Pricing() {
  const [showAll, setShowAll] = useState(false);
  const [plan, setPlan] = useState<'annual' | 'monthly'>('annual');

  function choosePlan(next: 'annual' | 'monthly') {
    setPlan(next);
    rememberPreferredPlan(next);
  }

  return (
    <section className="bg-white px-5 sm:px-8 py-16 sm:py-24" id="pricing">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="font-heading text-[28px] sm:text-[32px] text-[#1C2B16]">
            Start free. Grow at your own pace.
          </h2>
          <p className="font-body text-[16px] text-[#6B8F6E] mt-2 max-w-md mx-auto">
            {TAGLINE}
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-[800px] mx-auto">

          {/* Free */}
          <div className="bg-white border border-[#E8E0D0] rounded-xl p-8 flex flex-col gap-6">
            <div>
              <div className="font-heading text-[24px] text-[#1C2B16] mb-2">Free</div>
              <div className="font-heading text-[36px] text-[#2D5016] leading-none">$0</div>
              <div className="font-body text-[13px] text-[#6B8F6E] mt-1">
                Plan and grow with confidence
              </div>
            </div>
            <div className="border-t border-[#E8E0D0]" />
            <ul className="flex flex-col gap-2.5">
              {FREE_FEATURES.map(f => (
                <li key={f} className="flex items-start gap-2.5 font-body text-[14px] text-[#1C2B16]">
                  <CheckIcon /> {f}
                </li>
              ))}
            </ul>
            <div className="mt-auto pt-2">
              <Link
                href="/register"
                onClick={() => rememberSignupIntent('free')}
                className="block w-full text-center font-body text-[15px] font-semibold
                           text-[#2D5016] border-2 border-[#2D5016] py-3.5 rounded-xl
                           hover:bg-[#F0F4F0] transition-colors"
              >
                Start free
              </Link>
            </div>
          </div>

          {/* Pro */}
          <div className="relative bg-white border-2 border-[#2D5016] rounded-xl p-8 flex flex-col gap-6">
            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
              <span className="font-body text-[12px] font-semibold text-white bg-[#8B6F47]
                               px-4 py-1 rounded-full whitespace-nowrap">
                Most popular
              </span>
            </div>
            <div>
              <div className="font-heading text-[24px] text-[#1C2B16] mb-2">Garden Pro</div>
              {/* Annual leads. Gardening is seasonal, and monthly billing on a
                  seasonal product invites churn every autumn. */}
              <div className="font-heading text-[36px] text-[#2D5016] leading-none">
                {plan === 'annual' ? PRICE_ANNUAL : PRICE_MONTHLY}
                <span className="text-[18px] font-body font-normal">
                  /{plan === 'annual' ? 'year' : 'month'}
                </span>
              </div>
              <div className="font-body text-[13px] text-[#6B8F6E] mt-1">
                Free for {TRIAL_DAYS} days
                {plan === 'annual' && ', and two months cheaper than monthly'}
              </div>
              <button
                onClick={() => choosePlan(plan === 'annual' ? 'monthly' : 'annual')}
                className="mt-2 font-body text-[13px] text-[#2D5016] underline
                           underline-offset-2 hover:opacity-70 transition-opacity"
              >
                {plan === 'annual'
                  ? `or pay monthly at ${PRICE_MONTHLY}`
                  : `or pay yearly at ${PRICE_ANNUAL} and get two months free`}
              </button>
            </div>
            <div className="border-t border-[#E8E0D0]" />
            <ul className="flex flex-col gap-2.5">
              {PRO_HEADLINE_FEATURES.map(f => (
                <li key={f} className="flex items-start gap-2.5 font-body text-[14px] text-[#1C2B16]">
                  <CheckIcon /> {f}
                </li>
              ))}
              <li>
                <button
                  onClick={() => setShowAll(!showAll)}
                  className="font-body text-[13px] text-[#6B8F6E] hover:text-[#2D5016]
                             transition-colors underline underline-offset-2"
                >
                  {showAll ? 'Show fewer features' : 'See all Pro features'}
                </button>
              </li>
              {showAll && PRO_MORE_FEATURES.map(f => (
                <li key={f} className="flex items-start gap-2.5 font-body text-[14px] text-[#1C2B16]">
                  <CheckIcon /> {f}
                </li>
              ))}
            </ul>
            <div className="mt-auto pt-2">
              <Link
                href="/register"
                onClick={() => rememberSignupIntent('trial')}
                className="block w-full text-center font-body text-[15px] font-semibold
                           text-white bg-[#2D5016] py-3.5 rounded-xl
                           hover:opacity-90 transition-opacity"
              >
                Start your {TRIAL_DAYS}-day free trial
              </Link>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

// ── Final CTA ─────────────────────────────────────────────────────────────────

function FinalCTA() {
  return (
    <section className="bg-[#2D5016] px-5 sm:px-8 py-16 sm:py-24">
      <div className="max-w-3xl mx-auto text-center flex flex-col items-center gap-6">
        <h2 className="font-heading text-[28px] sm:text-[36px] text-white leading-[1.25]">
          Your garden is waiting.
        </h2>
        <p className="font-body text-[16px] sm:text-[18px] leading-[1.6] max-w-lg"
           style={{ color: 'rgba(255,255,255,0.8)' }}>
          Join gardeners who are growing with confidence, building living soil, and finally
          feeling like they know what they are doing.
        </p>
        <Link
          href="/register"
          className="font-body text-[17px] font-semibold text-[#2D5016] bg-white
                     px-10 py-4 rounded-xl hover:opacity-90 transition-opacity
                     inline-block"
        >
          Start your garden free
        </Link>
        <p className="font-body text-[13px]" style={{ color: 'rgba(255,255,255,0.65)' }}>
          Free to start. No credit card required. Cancel anytime.
        </p>
      </div>
    </section>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────

function Footer() {
  return (
    <footer className="bg-white border-t border-[#E8E0D0] px-5 sm:px-8 py-6">
      <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center
                      justify-between gap-3 text-center sm:text-left">
        <span className="font-body text-[14px] text-[#6B8F6E]">Garden by Willowbottom</span>
        <div className="flex items-center gap-4 flex-wrap justify-center">
          <Link
            href="/privacy"
            className="font-body text-[13px] text-[#6B8F6E] hover:text-[#2D5016] transition-colors"
          >
            Privacy Policy
          </Link>
          <Link
            href="/terms"
            className="font-body text-[13px] text-[#6B8F6E] hover:text-[#2D5016] transition-colors"
          >
            Terms of Service
          </Link>
          <Link
            href="mailto:garden@willowbottom.com"
            className="font-body text-[13px] text-[#6B8F6E] hover:text-[#2D5016] transition-colors"
          >
            Contact
          </Link>
          <Link
            href="/disclaimer"
            className="font-body text-[13px] text-[#6B8F6E] hover:text-[#2D5016] transition-colors"
          >
            Disclaimer
          </Link>
          <Link
            href="/philosophy"
            className="font-body text-[13px] text-[#6B8F6E] hover:text-[#2D5016] transition-colors"
          >
            Our Philosophy
          </Link>
        </div>
        <span className="font-body text-[13px] text-[#6B8F6E]">© 2026 Willowbottom</span>
      </div>
    </footer>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function LandingPage() {
  const router = useRouter();

  // Redirect logged-in users to their dashboard immediately
  useEffect(() => {
    if (getToken()) {
      router.replace('/dashboard');
    }
  }, [router]);

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <Header />
      <Hero />
      <TrustBar />
      <Problems />
      <Features />
      <Philosophy />
      <Pricing />
      <FinalCTA />
      <Footer />
    </div>
  );
}
